import React, { useState, useRef, useEffect } from 'react';

interface VideoSectionProps {
  videoSrc?: string;
  posterSrc?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
}

const HeroSection: React.FC<VideoSectionProps> = ({
  videoSrc = '/Images/Video.mp4', // Default video path
  posterSrc = '/images/hero-poster.jpg', // Default poster image
  autoPlay = true,
  muted = true,
  loop = true,
  controls = false
}) => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    if (videoRef.current && autoPlay) {
      videoRef.current.play().catch(() => {
        // Handle autoplay restrictions
        console.log('Autoplay prevented by browser');
      });
    }

    return () => window.removeEventListener('resize', checkMobile);
  }, [autoPlay]);

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  return (
    <section 
      ref={sectionRef}
      className="relative w-full h-screen md:h-screen overflow-hidden bg-black"
    >
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          className="w-full h-full object-cover md:object-cover"
          poster={posterSrc}
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          controls={controls}
          onLoadedData={handleVideoLoad}
          playsInline // Important for mobile
          preload="auto"
        >
          <source src={videoSrc} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Video Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-40 md:bg-opacity-40"></div>
      </div>

      {/* Loading State */}
      {!isVideoLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-30">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            <p className="text-white text-lg">Loading...</p>
          </div>
        </div>
      )}

      {/* Mobile Fallback - Show poster image on mobile if video fails */}
      {isMobile && !isVideoLoaded && (
        <div className="absolute inset-0 w-full h-full">
          <img 
            src={posterSrc} 
            alt="Hero background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;
